import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getBranchSmsConfig, deleteBranchSmsConfig, sendBranchTestSms } from '../../services/branchService'
import { apiError } from '../../api/apiError'
import type { BranchSmsConfig } from '../../types'
import BranchSmsConfigModal from './BranchSmsConfigModal'
import './BranchSmsConfig.css'

interface Props {
  branchId: string
  readOnly: boolean
  onToast: (msg: string, type: 'success' | 'error') => void
}

export default function BranchSmsCard({ branchId, readOnly, onToast }: Props) {
  const { t } = useTranslation()

  const [config, setConfig] = useState<BranchSmsConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showTest, setShowTest] = useState(false)
  const [testPhone, setTestPhone] = useState('')

  useEffect(() => {
    getBranchSmsConfig(branchId)
      .then(setConfig)
      .catch((err) => {
        console.error('[ERROR] Failed to load branch SMS config', err)
        onToast(t('admin.branch_sms.load_failed'), 'error')
      })
      .finally(() => setLoading(false))
    // перезагружаем только при смене филиала: onToast пересоздаётся родителем на каждом рендере
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId])

  async function handleDelete() {
    if (!confirm(t('admin.branch_sms.delete_confirm'))) return
    setBusy(true)
    try {
      await deleteBranchSmsConfig(branchId)
      setConfig(null)
      onToast(t('admin.branch_sms.deleted'), 'success')
    } catch (err) {
      onToast(apiError(err).message || t('admin.branch_sms.delete_failed'), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function handleTest() {
    if (!testPhone.trim()) return
    setBusy(true)
    try {
      const res = await sendBranchTestSms(branchId, testPhone.trim())
      onToast(t('admin.branch_sms.test_sent', { phone: res.phone, sid: res.messageSid }), 'success')
      setShowTest(false)
      setTestPhone('')
    } catch (err) {
      const { code, message } = apiError(err)
      const errorMap: Record<string, string> = {
        PHONE_INVALID: t('admin.branch_sms.invalid_phone'),
        SMS_AUTH_FAILED: t('admin.branch_sms.auth_failed'),
        SMS_RECIPIENT_UNSUBSCRIBED: t('admin.branch_sms.unsubscribed'),
      }
      onToast((code && errorMap[code]) || message || t('admin.branch_sms.test_failed'), 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="admin-detail-card branch-sms">
      <div className="branch-sms__header">
        <p className="admin-detail-card__title">{t('admin.branch_sms.card_title')}</p>
        {!loading && (
          <span className={`branch-sms__status branch-sms__status--${config ? (config.enabled ? 'on' : 'off') : 'none'}`}>
            {config ? (config.enabled ? t('admin.branch_sms.status_enabled') : t('admin.branch_sms.status_disabled')) : t('admin.branch_sms.status_not_configured')}
          </span>
        )}
      </div>

      {loading ? (
        <div className="admin-spinner" />
      ) : !config ? (
        <p className="branch-sms__empty">{t('admin.branch_sms.not_configured_hint')}</p>
      ) : (
        <div className="admin-detail-grid">
          <Field label={t('admin.branch_sms.label_from_number')} value={config.fromNumber} />
          <Field label={t('admin.branch_sms.label_account_sid')} value={config.accountSid} />
          <Field label={t('admin.branch_sms.label_api_key_sid')} value={config.apiKeySid} />
          <Field label={t('admin.branch_sms.label_api_key_secret')} value={config.apiKeySecretSet ? t('admin.branch_sms.secret_saved') : '—'} />
          <Field label={t('admin.branch_sms.field_updated')} value={formatDate(config.updatedAt)} />
        </div>
      )}

      {!loading && !readOnly && (
        <div className="branch-sms__actions">
          <button className="admin-btn admin-btn--primary" disabled={busy} onClick={() => setShowModal(true)}>
            {config ? t('admin.branch_sms.edit') : t('admin.branch_sms.configure')}
          </button>
          {config && !showTest && (
            <button className="admin-btn admin-btn--secondary" disabled={busy || !config.enabled} onClick={() => setShowTest(true)}>
              {t('admin.branch_sms.send_test')}
            </button>
          )}
          {config && (
            <button className="admin-btn admin-btn--danger" disabled={busy} onClick={handleDelete}>
              {t('admin.branch_sms.delete')}
            </button>
          )}
        </div>
      )}

      {showTest && (
        <div className="branch-sms__test">
          <input
            className="admin-input"
            placeholder={t('admin.branch_sms.test_phone_placeholder')}
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleTest()}
            autoFocus
            disabled={busy}
          />
          <button className="admin-btn admin-btn--primary" onClick={handleTest} disabled={busy || !testPhone.trim()}>
            {busy ? t('admin.branch_sms.sending') : t('admin.branch_sms.send')}
          </button>
          <button className="admin-btn admin-btn--secondary" onClick={() => { setShowTest(false); setTestPhone('') }} disabled={busy}>
            {t('admin.branch_sms.cancel')}
          </button>
        </div>
      )}

      {showModal && (
        <BranchSmsConfigModal
          branchId={branchId}
          config={config}
          onClose={() => setShowModal(false)}
          onSaved={(saved) => { setConfig(saved); setShowModal(false); onToast(t('admin.branch_sms.saved'), 'success') }}
        />
      )}
    </div>
  )
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="admin-detail-field">
      <span className="admin-detail-field__label">{label}</span>
      <span className="admin-detail-field__value">{value || '—'}</span>
    </div>
  )
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}
